/**
 * Auth Store - Reactive Authentication State Singleton
 *
 * Provides reactive state management for authentication status and user data.
 * Used for route guarding and displaying user context throughout the app.
 *
 * Token refresh resilience features:
 * - Scheduled refresh before token expiration
 * - Visibility change detection (background tab recovery)
 * - Window focus detection (backup for edge cases)
 * - Network online detection (recovery after connectivity loss)
 * - Cross-tab coordination via BroadcastChannel (prevents duplicate refreshes, syncs logout)
 *
 * @example
 * import { authStore } from '@/services/gateway';
 *
 * // Check authentication status
 * authStore.status.map(s => s === 'authenticated' ? showDashboard() : showLogin())
 *
 * // Access current user
 * authStore.user.map(user => user ? `Hello, ${user.displayName}` : 'Guest')
 */

import { prop, Signal } from '@tempots/dom'
import type { UserResponse, AccountResponse } from '../services/types'
import type { OAuthService } from '../services/oauth.service'
import { setSessionValidity } from '../services/gateway'

// ============================================================================
// Types
// ============================================================================

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export interface AuthState {
  status: AuthStatus
  user: UserResponse | null
  account: AccountResponse | null
  error: string | null
}

export interface AuthStore {
  /** Current auth status - reactive */
  readonly status: Signal<AuthStatus>

  /** Current user - reactive, null if not authenticated */
  readonly user: Signal<UserResponse | null>

  /** Current account - reactive, null if not authenticated */
  readonly account: Signal<AccountResponse | null>

  /** Last error message - reactive */
  readonly error: Signal<string | null>

  /** Whether user is authenticated (convenience getter) */
  readonly isAuthenticated: Signal<boolean>

  /** Whether auth state is loading */
  readonly isLoading: Signal<boolean>

  /**
   * Set authenticated state after login
   * @param user - User data
   * @param account - Optional account data
   * @param expiresAt - Optional token expiration time (ISO string)
   */
  setAuthenticated(
    user: UserResponse,
    account?: AccountResponse,
    expiresAt?: string
  ): void

  /**
   * Clear auth state (logout)
   * @param broadcast - Whether to notify other tabs (default: true)
   */
  clear(broadcast?: boolean): void

  /**
   * Refresh user data from server
   */
  refresh(): Promise<void>

  /**
   * Stop automatic token refresh (cleanup)
   */
  dispose(): void
}

// ============================================================================
// Constants
// ============================================================================

/** Refresh token 5 minutes before expiration */
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

/** Minimum refresh interval (1 minute) */
const MIN_REFRESH_INTERVAL_MS = 60 * 1000

/** Default token lifetime if not specified (10 minutes, conservative fallback for 15-minute tokens) */
const DEFAULT_TOKEN_LIFETIME_MS = 10 * 60 * 1000

/** Maximum number of token refresh retries */
const MAX_REFRESH_RETRIES = 3

/** BroadcastChannel name for cross-tab token coordination */
const TOKEN_CHANNEL_NAME = 'verts-auth-token-refresh'

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create the auth store singleton
 */
export function createAuthStore(oauthService: OAuthService): AuthStore {
  // Internal reactive state
  const status = prop<AuthStatus>('loading')
  const user = prop<UserResponse | null>(null)
  const account = prop<AccountResponse | null>(null)
  const error = prop<string | null>(null)

  // Derived values
  const isAuthenticated = status.map((s) => s === 'authenticated')
  const isLoading = status.map((s) => s === 'loading')

  // Token refresh timer
  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  // Track token expiration for visibility-based refresh
  let tokenExpiresAt: number | null = null

  // Mutex to prevent concurrent token refreshes (race condition fix)
  let isRefreshing = false
  let refreshRetryCount = 0

  // BroadcastChannel for cross-tab coordination (may not be available in all browsers)
  let tokenChannel: BroadcastChannel | null = null
  try {
    tokenChannel = new BroadcastChannel(TOKEN_CHANNEL_NAME)
  } catch {
    // BroadcastChannel not supported (e.g., Safari < 15.4, some WebViews)
  }

  /**
   * Schedule token refresh before expiration
   */
  function scheduleTokenRefresh(expiresAt?: string): void {
    // Clear any existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    let refreshIn: number

    if (expiresAt) {
      const expiresAtMs = new Date(expiresAt).getTime()
      tokenExpiresAt = expiresAtMs
      const now = Date.now()
      refreshIn = expiresAtMs - now - TOKEN_REFRESH_BUFFER_MS
    } else {
      // Use default lifetime if no expiration provided (shouldn't happen with fixed backend)
      console.warn(
        'Token expiration not provided, using conservative default of',
        DEFAULT_TOKEN_LIFETIME_MS / 1000 / 60,
        'minutes'
      )
      tokenExpiresAt = Date.now() + DEFAULT_TOKEN_LIFETIME_MS
      refreshIn = DEFAULT_TOKEN_LIFETIME_MS
    }

    // Ensure minimum refresh interval
    refreshIn = Math.max(refreshIn, MIN_REFRESH_INTERVAL_MS)

    refreshTimer = setTimeout(async () => {
      await performTokenRefresh()
    }, refreshIn)
  }

  /**
   * Check if token needs refresh and trigger if necessary
   */
  function checkAndRefreshIfNeeded(): void {
    // Only check if we're authenticated and have a known expiration
    if (status.get() !== 'authenticated' || tokenExpiresAt === null) {
      return
    }

    const now = Date.now()
    const timeUntilExpiry = tokenExpiresAt - now

    // If token is expired or will expire within the buffer window, refresh immediately
    if (timeUntilExpiry <= TOKEN_REFRESH_BUFFER_MS) {
      performTokenRefresh()
    }
  }

  /**
   * Handle tab visibility change - refresh token if it may have expired while backgrounded
   */
  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      checkAndRefreshIfNeeded()
    }
  }

  /**
   * Handle window focus - backup for visibility change (some edge cases)
   */
  function handleWindowFocus(): void {
    checkAndRefreshIfNeeded()
  }

  /**
   * Handle network coming back online - retry refresh if token may be stale
   */
  function handleOnline(): void {
    // Reset retry count when network comes back - give it fresh attempts
    refreshRetryCount = 0
    checkAndRefreshIfNeeded()
  }

  /**
   * Internal clear logic (used by both clear() and cross-tab logout)
   */
  function clearInternal(): void {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
    tokenExpiresAt = null
    user.set(null)
    account.set(null)
    error.set(null)
    status.set('unauthenticated')
    setSessionValidity(false)
  }

  /**
   * Handle messages from other tabs via BroadcastChannel
   */
  function handleTokenChannelMessage(event: MessageEvent): void {
    if (event.data?.type === 'token-refreshed' && event.data.expiresAt) {
      // Another tab refreshed the token - update our expiration and reschedule
      const expiresAtMs = new Date(event.data.expiresAt).getTime()
      tokenExpiresAt = expiresAtMs
      scheduleTokenRefresh(event.data.expiresAt)
    } else if (event.data?.type === 'logout') {
      // Another tab logged out - clear our state too (without broadcasting back)
      clearInternal()
    }
  }

  // Register event listeners
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleWindowFocus)
  window.addEventListener('online', handleOnline)
  if (tokenChannel) {
    tokenChannel.onmessage = handleTokenChannelMessage
  }

  /**
   * Perform token refresh with mutex to prevent race conditions
   */
  async function performTokenRefresh(): Promise<void> {
    // Prevent concurrent refresh attempts (race condition fix)
    if (isRefreshing) {
      return
    }

    isRefreshing = true
    try {
      const result = await oauthService.refreshToken()
      // Reset retry count on success
      refreshRetryCount = 0
      // Schedule next refresh based on new expiration
      scheduleTokenRefresh(result.expiresAt)
      // Notify other tabs about the refresh so they don't duplicate the work
      if (tokenChannel) {
        tokenChannel.postMessage({
          type: 'token-refreshed',
          expiresAt: result.expiresAt,
        })
      }
    } catch (e) {
      refreshRetryCount++

      // Retry with exponential backoff if we haven't exceeded max retries
      if (refreshRetryCount < MAX_REFRESH_RETRIES) {
        const retryDelay = Math.min(
          1000 * Math.pow(2, refreshRetryCount),
          30000
        )
        console.warn(
          `Token refresh failed (attempt ${refreshRetryCount}/${MAX_REFRESH_RETRIES}), retrying in ${retryDelay}ms:`,
          e
        )
        refreshTimer = setTimeout(async () => {
          await performTokenRefresh()
        }, retryDelay)
      } else {
        // Token refresh failed after all retries - user needs to re-authenticate
        console.warn('Token refresh failed after all retries:', e)
        refreshRetryCount = 0
        user.set(null)
        account.set(null)
        status.set('unauthenticated')
        setSessionValidity(false)
        error.set('Session expired. Please sign in again.')
      }
    } finally {
      isRefreshing = false
    }
  }

  const initialize = async (): Promise<void> => {
    status.set('loading')
    error.set(null)

    try {
      const currentUser = await oauthService.getCurrentUser()

      if (currentUser) {
        user.set(currentUser)
        status.set('authenticated')
        setSessionValidity(true)
        // Start token refresh cycle (use default timing since we don't know current expiration)
        scheduleTokenRefresh()
      } else {
        user.set(null)
        account.set(null)
        status.set('unauthenticated')
        setSessionValidity(false)
      }
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to check authentication'
      error.set(message)
      user.set(null)
      account.set(null)
      status.set('unauthenticated')
      setSessionValidity(false)
    }
  }

  initialize()

  return {
    status,
    user,
    account,
    error,
    isAuthenticated,
    isLoading,
    clear(broadcast = true): void {
      clearInternal()
      // Notify other tabs to also log out
      if (broadcast && tokenChannel) {
        tokenChannel.postMessage({ type: 'logout' })
      }
    },

    setAuthenticated(
      newUser: UserResponse,
      newAccount?: AccountResponse,
      expiresAt?: string
    ): void {
      user.set(newUser)
      if (newAccount) {
        account.set(newAccount)
      }
      error.set(null)
      status.set('authenticated')
      setSessionValidity(true)
      // Start token refresh cycle
      scheduleTokenRefresh(expiresAt)
    },

    async refresh(): Promise<void> {
      try {
        const currentUser = await oauthService.getCurrentUser()

        if (currentUser) {
          user.set(currentUser)
          status.set('authenticated')
          setSessionValidity(true)
        } else {
          user.set(null)
          account.set(null)
          status.set('unauthenticated')
          setSessionValidity(false)
        }
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to refresh user'
        error.set(message)
      }
    },

    dispose(): void {
      if (refreshTimer) {
        clearTimeout(refreshTimer)
        refreshTimer = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
      window.removeEventListener('online', handleOnline)
      if (tokenChannel) {
        tokenChannel.close()
        tokenChannel = null
      }
    },
  }
}
